// external imports
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';

// internal imports
import {
	fetchSiteCapacity,
	addSiteCapacity,
	updateSiteCapacity,
	deleteSiteCapacity,
	updateCaptureCapacities
} from 'actions';

const allowInitialCapacityDeletion = false;

export const FormCapacityTable = ({ site, enableValidation = true }) => {
	const dispatch = useDispatch();
	const [editingIndex, setEditingIndex] = useState(null);
	const [editedRow, setEditedRow] = useState(null);
	const [saving, setSaving] = useState(false);
	const [validationError, setValidationError] = useState('');
	const [updatingCaptureCapacities, setUpdatingCaptureCapacities] = useState(false);
	const [newRow, setNewRow] = useState({
		capacity: '',
		captured: ''
	});

	const capacities = useSelector((state) => state.siteCapacities) || [];
	const siteId = site && site.id;
	const earliestRow = capacities.reduce(
		(earliest, row) => !earliest || row.captured.slice(0, 10) < earliest.captured.slice(0, 10) ? row : earliest,
		null
	);
	const initialRow = earliestRow && earliestRow.captured.slice(0, 10) <= '2016-01-01'
		? earliestRow : null;
	const getMatchingNeighbor = (data, originalRow = null) => {
		if (!enableValidation || !data || String(data.capacity).trim() === '' ||
			!moment(data.captured, 'YYYY-MM-DD', true).isValid()) return '';
		let precedingRow = null;
		let followingRow = null;
		capacities.forEach((row) => {
			if (row === originalRow) return;
			const captured = row.captured.slice(0, 10);
			if (captured < data.captured &&
				(!precedingRow || captured > precedingRow.captured.slice(0, 10))) precedingRow = row;
			if (captured > data.captured &&
				(!followingRow || captured < followingRow.captured.slice(0, 10))) followingRow = row;
		});
		if (precedingRow && Number(data.capacity) === Number(precedingRow.capacity)) return 'preceding';
		if (followingRow && Number(data.capacity) === Number(followingRow.capacity)) return 'following';
		return '';
	};
	const newMatchingNeighbor = getMatchingNeighbor(newRow);
	const editedMatchingNeighbor = editingIndex !== null ?
		getMatchingNeighbor(editedRow, capacities[editingIndex]) : '';
	const getUnchangedCapacityMessage = (neighbor) =>
		`Capacity is unchanged from the ${neighbor} record. Enter a different capacity to save the record.`;
	const deleteInitialReason = 'The initial capacity record cannot be deleted because it establishes the starting capacity.';
	const today = new Intl.DateTimeFormat('en-CA', {
		timeZone: 'Europe/London',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit'
	}).format(new Date());

	useEffect(() => {
		setEditingIndex(null);
		setEditedRow(null);
		setValidationError('');
		if (siteId == null) return;
		dispatch(fetchSiteCapacity({ id: siteId }));
	}, [dispatch, siteId]);

	const validateForm = (thisData, originalRow = null) => {
		if (!enableValidation) {
			setValidationError('');
			return true;
		}
		let error = '';
		if (String(thisData.capacity).trim() === '' ||
			!Number.isSafeInteger(Number(thisData.capacity)) || Number(thisData.capacity) < 0) {
			error = 'Capacity must be a whole number of zero or greater.';
		} else if (!moment(thisData.captured, 'YYYY-MM-DD', true).isValid() ||
			thisData.captured > today) {
			error = 'Enter a valid captured date that is not in the future.';
		} else if (originalRow && originalRow === initialRow && thisData.captured > '2016-01-01') {
			error = 'The initial capacity captured date must be on or before 2016-01-01.';
		} else if (initialRow && originalRow !== initialRow &&
			thisData.captured <= formatDate(initialRow.captured)) {
			error = 'Capacity must remain after the initial capacity record.';
		} else if (capacities.some((row) => row !== originalRow &&
			formatDate(row.captured) === thisData.captured)) {
			error = 'A capacity record already exists for this captured date.';
		} else if (initialRow && originalRow === initialRow && capacities.some((row) =>
			row !== originalRow && formatDate(row.captured) <= thisData.captured)) {
			error = 'The initial capacity record must remain the earliest record.';
		} else if (getMatchingNeighbor(thisData, originalRow)) {
			error = getUnchangedCapacityMessage(getMatchingNeighbor(thisData, originalRow));
		}
		setValidationError(error);
		return !error;
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setEditedRow((prev) => ({ ...prev, [name]: value }));
		setValidationError('');
	};

	const handleNewChange = (e) => {
		const { name, value } = e.target;
		setNewRow((prev) => ({ ...prev, [name]: value }));
		setValidationError('');
	};

	const handleAdd = async (e) => {
		e.preventDefault();

		const formValid = validateForm(newRow);

		if (formValid) {
			const payload = {
				capacity: newRow.capacity,
				captured: `${newRow.captured} 00:00:00`
			};

			dispatch(addSiteCapacity({
				id: site.id,
				body: payload
			}));
			setNewRow({ capacity: '', captured: '' });
		} else {
			// form not valid
			console.error('Form not valid');
		}
	};

	const handleDelete = async (e, row) => {
		e.preventDefault();
		if ((!allowInitialCapacityDeletion && row === initialRow) || saving || editingIndex !== null) return;
		dispatch(deleteSiteCapacity({ id: site.id, date: row.captured }));
	};

	const handleUpdateCaptureCapacities = async () => {
		setUpdatingCaptureCapacities(true);
		try {
			await dispatch(updateCaptureCapacities({ id: site.id }));
		} finally {
			setUpdatingCaptureCapacities(false);
		}
	};

	const handleEdit = (row, idx) => {
		setEditingIndex(idx);
		setEditedRow({ capacity: row.capacity, captured: formatDate(row.captured) });
		setValidationError('');
	};

	const handleCancel = () => {
		setEditingIndex(null);
		setEditedRow(null);
		setValidationError('');
	};

	const handleSave = async (row) => {
		if (saving || !validateForm(editedRow, row)) return;
		setSaving(true);
		try {
			const success = await dispatch(updateSiteCapacity({
				id: site.id,
				date: row.captured,
				body: {
					capacity: enableValidation ? Number(editedRow.capacity) : editedRow.capacity,
					captured: `${editedRow.captured} 00:00:00`
				}
			}));
			if (success) handleCancel();
		} finally {
			setSaving(false);
		}
	};

	const formatDate = (date) => {
		let formattedDate = date.slice(0, 10);

		return formattedDate;
	};

	return (
		<div className="p-3">
			{validationError && <div className="alert alert-danger" role="alert">{validationError}</div>}
			<table className="table table-sm table-borderless formCapacityTable">
				<thead>
					<tr>
						<th>Capacity</th>
						<th>Captured</th>
						<th>Actions</th>
					</tr>
				</thead>
				<tbody>
					{capacities && (
						<>
							{capacities.map((row, idx) => (
								<tr key={idx}>
									{editingIndex === idx ? (
										<>
											<td>
												<input
													className="form-control"
													name="capacity"
													type="number"
													step={enableValidation ? '1' : 'any'}
													min={enableValidation ? '0' : undefined}
													value={editedRow.capacity}
													disabled={saving}
													aria-label="Edit capacity"
													onChange={handleChange}
												/>
											</td>
											<td>
												<input
													className="form-control"
													type="date"
													name="captured"
													value={editedRow.captured}
													max={enableValidation ? (row === initialRow ? '2016-01-01' : today) : undefined}
													disabled={saving}
													aria-label="Edit captured date"
													onChange={handleChange}
												/>
											</td>
										</>
									) : (
										<>
											<td>{row.capacity}</td>
											<td>{formatDate(row.captured)}</td>
										</>
									)}

									<td className="d-flex align-items-evenly">
										{editingIndex === idx ? (
											<>
												<button
													type="button"
													className="btn btn-info btn-sm"
													title="Save edited changes"
													disabled={saving || Boolean(editedMatchingNeighbor)}
													aria-describedby={editedMatchingNeighbor ? 'unchangedCapacityMessage' : undefined}
													onClick={() => handleSave(row)}
												>
													{saving ? 'Saving...' : 'Save'}
												</button>
												<button
													type="button"
													className="btn btn-secondary btn-sm mx-2"
													disabled={saving}
													onClick={handleCancel}
												>
													Cancel
												</button>
											</>
										) : (
											<>
												<button
													type="button"
													className="btn btn-info btn-sm"
													title="Edit capacity record"
													disabled={editingIndex !== null || saving}
													onClick={() => handleEdit(row, idx)}
												>
													Edit
												</button>
												<span
													className="d-inline-block mx-2"
													title={!allowInitialCapacityDeletion && row === initialRow ? deleteInitialReason : undefined}
													tabIndex={!allowInitialCapacityDeletion && row === initialRow ? 0 : undefined}
													aria-label={!allowInitialCapacityDeletion && row === initialRow ? deleteInitialReason : undefined}
												>
													<button
														type="button"
														className="btn btn-info btn-sm"
														title="Delete capacity record"
														aria-label="Delete capacity record"
														disabled={(!allowInitialCapacityDeletion && row === initialRow) || editingIndex !== null || saving}
														style={!allowInitialCapacityDeletion && row === initialRow ? { pointerEvents: 'none' } : undefined}
														onClick={(e) => handleDelete(e, row)}
													>
														<i className="fa-solid fa-trash"></i>
													</button>
												</span>
											</>
										)}
									</td>
								</tr>
							))}

							<tr>
								<td>
									<input
										className="form-control"
										name="capacity"
										type="number"
										step={enableValidation ? '1' : 'any'}
										value={newRow.capacity}
										onChange={handleNewChange}
										placeholder="Capacity"
									/>
								</td>
								<td>
									<input
										className="form-control"
										type="date"
										min={enableValidation && initialRow ? initialRow.captured.slice(0, 10) : undefined}
										max={enableValidation ? today : undefined}
										name="captured"
										value={newRow.captured}
										onChange={handleNewChange}
										placeholder="Captured"
									/>
								</td>

								<td>
									<button
										type="button"
										className="btn btn-info"
										title="Add capacity record"
										disabled={editingIndex !== null || saving || !site || Boolean(newMatchingNeighbor)}
										aria-describedby={newMatchingNeighbor ? 'unchangedCapacityMessage' : undefined}
										onClick={(e) => handleAdd(e)}
									>
										Add
									</button>
								</td>
							</tr>
						</>
					)}
				</tbody>
			</table>
			{(newMatchingNeighbor || editedMatchingNeighbor) && (
				<div id="unchangedCapacityMessage" className="alert alert-warning" role="status">
					{getUnchangedCapacityMessage(editedMatchingNeighbor || newMatchingNeighbor)}
				</div>
			)}
			<div className="text-center mt-3">
				<button
					type="button"
					className="btn btn-info"
					disabled={!site || updatingCaptureCapacities}
					onClick={handleUpdateCaptureCapacities}
				>
					{updatingCaptureCapacities ? 'Updating tables...' : 'Update Processing and Index tables'}
				</button>
			</div>
		</div>
	);
};
